import { useQuery } from '@apollo/client';
import {
    ServiceRequestQuestionCatalogDocument,
    type ServiceRequestQuestionCatalogQuery,
} from '~/gql/ucc/graphql';
import type { PxFragenkatalog } from './types';

interface UseServiceRequestQuestionCatalogResult {
    catalog: PxFragenkatalog | undefined;
    loading: boolean;
    error: Error | undefined;
    /**
     * True, wenn der Katalog nicht live vom SVC kam, sondern aus dem Snapshot-Fallback
     * (UCPWO-398). Der Dialog muss das anzeigen — siehe `PxConnectModal`.
     */
    fromSnapshot: boolean;
    /** Stand des Fallbacks (ISO-8601), nur bei `fromSnapshot`. */
    snapshotSavedAt: string | undefined;
}

type CatalogQuestions = NonNullable<
    ServiceRequestQuestionCatalogQuery['serviceRequestQuestionCatalog']
>['questions'];

/**
 * Coerce the GraphQL `QuestionCatalog` (whose fields mirror the PX DTO optionality
 * and are therefore mostly nullable) into the renderer's non-null `PxQuestion[]`.
 * Answer `uid`s are NOT assigned here — the reducer does that on `INIT`
 * (see `assignAnswerUids`), keeping a single place that owns identity.
 */
const toPxFragenkatalog = (questions: CatalogQuestions): PxFragenkatalog =>
    questions.map((question) => ({
        key: question.key,
        order: question.order ?? 0,
        question: question.question ?? '',
        parentKey: question.parentKey ?? '',
        isMultipleAnswer: question.isMultipleAnswer ?? false,
        isMandatory: question.isMandatory ?? false,
        answers: (question.answers ?? []).map((answer) => ({
            key: answer.key,
            answer: answer.answer ?? '',
            isSelected: answer.isSelected ?? false,
            hint: answer.hint ?? null,
            comment: answer.comment ?? null,
            quantity: answer.quantity ?? 0,
            quantityDescription: answer.quantityDescription ?? null,
            withQuantity: answer.withQuantity ?? false,
            withComment: answer.withComment ?? false,
            relatedQuestions: answer.relatedQuestions ?? null,
        })),
    }));

/**
 * Fetch the PX Connect Fragenkatalog for a subproject's `serviceRequestId`
 * (UCPWO-378). The backend (`serviceRequestQuestionCatalog`) derives the insurer
 * from the service request itself, so the client only needs the id.
 *
 * Runs against the service API (default Apollo link — no `clientName: 'ucpw'`).
 * Pass `skip` to avoid loading until the modal is open; an absent id also skips.
 */
export const useServiceRequestQuestionCatalog = (
    serviceRequestId: string | undefined,
    options?: { skip?: boolean }
): UseServiceRequestQuestionCatalogResult => {
    const skip = (options?.skip ?? false) || !serviceRequestId;

    const { data, loading, error } = useQuery(ServiceRequestQuestionCatalogDocument, {
        variables: { serviceRequestId: serviceRequestId as string },
        skip,
    });

    const result = data?.serviceRequestQuestionCatalog;
    const questions = result?.questions;
    const catalog = questions ? toPxFragenkatalog(questions) : undefined;

    return {
        catalog,
        loading,
        error,
        // Nur melden, wenn wirklich ein Katalog daraus wurde: ohne Fragen zeigt der Dialog
        // den Leerzustand, und ein „Stand vom …"-Hinweis darüber wäre irreführend.
        fromSnapshot: Boolean(catalog?.length && result?.fromSnapshot),
        snapshotSavedAt: result?.snapshotSavedAt ?? undefined,
    };
};
